// ** MUI Imports
import { Direction } from '@mui/material'
import {
    Skin,
    Mode,
    AppBar,
    Footer,
    ThemeColor,
    ContentWidth,
    VerticalNavToggle
} from '../layouts/types'
import themeConfig from '../configs/themeConfig'
import { createContext, useEffect, useState, ReactNode } from "react";

export type Settings = {
    skin: Skin
    mode: Mode
    appBar?: AppBar
    footer?: Footer
    navHidden?: boolean // navigation menu
    appBarBlur: boolean
    direction: Direction
    navCollapsed: boolean
    themeColor: ThemeColor
    contentWidth: ContentWidth
    layout?: 'vertical' | 'horizontal'
    lastLayout?: 'vertical' | 'horizontal'
    verticalNavToggleType: VerticalNavToggle
    platform: PlatformType
    toastPosition?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right'
}

export type SettingsContextValue = {
    settings: Settings
    saveSettings: (updatedSettings: Settings) => void
}

export type PlatformType = 'tt' | 'cg' | 'ms' | '';

export type PageSpecificSettings = {
    skin?: Skin
    mode?: Mode
    appBar?: AppBar
    footer?: Footer
    navHidden?: boolean // navigation menu
    appBarBlur?: boolean
    direction?: Direction
    navCollapsed?: boolean
    themeColor?: ThemeColor
    contentWidth?: ContentWidth
    layout?: 'vertical' | 'horizontal'
    lastLayout?: 'vertical' | 'horizontal'
    verticalNavToggleType?: VerticalNavToggle
    toastPosition?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right'
}

interface SettingsProviderProps {
    children: ReactNode
    pageSettings?: PageSpecificSettings | void
}

export type BossItem = {
    label: PlatformType
    value: string
}

export type NavSectionTitle = {
    action?: string
    subject?: string
    sectionTitle: string
}

const initialSettings: Settings = {
    themeColor: 'primary',
    platform: '',
    mode: themeConfig.mode,
    skin: themeConfig.skin,
    footer: themeConfig.footer,
    layout: themeConfig.layout,
    lastLayout: themeConfig.layout,
    direction: themeConfig.direction,
    navHidden: themeConfig.navHidden,
    appBarBlur: themeConfig.appBarBlur,
    navCollapsed: themeConfig.navCollapsed,
    contentWidth: themeConfig.contentWidth,
    toastPosition: themeConfig.toastPosition,
    verticalNavToggleType: themeConfig.verticalNavToggleType,
    appBar: themeConfig.layout === 'horizontal' && themeConfig.appBar === 'hidden' ? 'fixed' : themeConfig.appBar
}

const staticSettings = {
    appBar: initialSettings.appBar,
    footer: initialSettings.footer,
    layout: initialSettings.layout,
    navHidden: initialSettings.navHidden,
    lastLayout: initialSettings.lastLayout,
    toastPosition: initialSettings.toastPosition
}

// ** Create Context
export const SettingsContext = createContext<SettingsContextValue>({
    saveSettings: () => null,
    settings: initialSettings
})

// set settings in localStorage
const storeSettings = (settings: Settings) => {
    const initSettings = Object.assign({}, settings)

    delete initSettings.appBar
    delete initSettings.footer
    delete initSettings.layout
    delete initSettings.navHidden
    delete initSettings.lastLayout
    delete initSettings.toastPosition
    window.localStorage.setItem('settings', JSON.stringify(initSettings))
}

const restoreSettings = (): Settings | null => {
    let settings = null

    try {
        const storedData: string | null = window.localStorage.getItem('settings')
        console.log("storedData", storedData);
        if (storedData) {
            settings = { ...JSON.parse(storedData), ...staticSettings }
        } else {
            settings = initialSettings
        }
    } catch (err) {
        console.error(err)
    }

    return settings
}

export const SettingsProvider = ({ children, pageSettings }: SettingsProviderProps) => {
    // ** State
    const [settings, setSettings] = useState<Settings>({ ...initialSettings })

    useEffect(() => {
        const restoredSettings = restoreSettings()
        // console.log("restoredSettings", restoredSettings)

        if (restoredSettings) {
            setSettings({ ...restoredSettings, contentWidth: 'full' })
        }
        if (pageSettings) {
            setSettings({ ...settings, ...pageSettings })
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageSettings])

    useEffect(() => {
        if (settings.layout === 'horizontal' && settings.mode === 'semi-dark') {
            saveSettings({ ...settings, mode: 'light' })
        }
        if (settings.layout === 'horizontal' && settings.appBar === 'hidden') {
            saveSettings({ ...settings, appBar: 'fixed' })
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings.layout])

    const saveSettings = (updatedSettings: Settings) => {
        console.log("updatedSettings", updatedSettings);
        storeSettings(updatedSettings)
        setSettings(updatedSettings)
    }

    return <SettingsContext.Provider value={{ settings, saveSettings }}>{children}</SettingsContext.Provider>
}

export const SettingsConsumer = SettingsContext.Consumer
